import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  groupTopChartItems,
  rankedBarChartHeight,
  truncateChartLabel,
} from "../../utils/chartData";
import DashboardGroupedChartHint from "./DashboardGroupedChartHint";

const HORIZONTAL_THRESHOLD = 7;

/**
 * Bar chart for categorical data: groups long tails, switches to horizontal layout when crowded.
 */
export default function DashboardRankedBarChart({
  data = [],
  categoryKey,
  valueKey,
  valueName,
  fill,
  height = 280,
  maxCategories = 15,
  othersLabel = "Outros",
  formatValue,
  formatTooltip,
}) {
  const prepared = useMemo(
    () =>
      groupTopChartItems(data, {
        maxItems: maxCategories,
        valueKey,
        nameKey: categoryKey,
        othersLabel,
      }),
    [data, maxCategories, categoryKey, valueKey, othersLabel]
  );

  const horizontal = prepared.items.length > HORIZONTAL_THRESHOLD;
  const chartHeight = horizontal
    ? rankedBarChartHeight(prepared.items.length, { min: height, max: 520 })
    : height;

  const tooltipFormatter = formatTooltip || (formatValue ? (v) => formatValue(v) : undefined);

  if (!prepared.items.length) return null;

  return (
    <>
      {prepared.grouped ? (
        <DashboardGroupedChartHint shown={prepared.items.length} total={prepared.totalCount} />
      ) : null}
      <ResponsiveContainer width="100%" height={chartHeight}>
        {horizontal ? (
          <BarChart
            data={prepared.items}
            layout="vertical"
            margin={{ left: 4, right: 16, top: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={formatValue} />
            <YAxis
              type="category"
              dataKey={categoryKey}
              width={130}
              tick={{ fontSize: 11 }}
              tickFormatter={(label) => truncateChartLabel(label, 20)}
            />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={(label) => label}
            />
            <Bar dataKey={valueKey} name={valueName} fill={fill} />
          </BarChart>
        ) : (
          <BarChart data={prepared.items} margin={{ bottom: 56 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={categoryKey}
              tick={{ fontSize: 11 }}
              interval={0}
              angle={-22}
              textAnchor="end"
              height={56}
              tickFormatter={(label) => truncateChartLabel(label, 14)}
            />
            <YAxis tickFormatter={formatValue} />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={(label) => label}
            />
            <Bar dataKey={valueKey} name={valueName} fill={fill} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </>
  );
}
